import type { RefObject } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { DATA_TABLE_DEFAULT_VIEWPORT_MAX_HEIGHT_PX, parseViewportLengthToPx } from './dataTableViewport';

const DEFAULT_BOTTOM_INSET_PX = 50;

interface UseFillAvailableViewportHeightArgs {
    enabled: boolean;
    viewportMaxHeight?: string | number;
    viewportMinHeight?: string | number;
    /** Pixels reserved below the table (safe area / breathing room above window bottom). */
    viewportBottomInsetPx?: number;
}

/** Nearest ancestor that owns vertical scrolling (RAM shell: `<main class="… overflow-auto">`). */
function findVerticalScrollport(start: HTMLElement): HTMLElement | null {
    let el: HTMLElement | null = start.parentElement;
    while (el && el !== document.documentElement) {
        const { overflowY } = window.getComputedStyle(el);
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}

/**
 * Computes the available height from the element's current position to the bottom of
 * the visible scroll region. This accounts for:
 * - The scrollport (e.g. `<main class="overflow-auto">`) if present
 * - The window viewport
 * - The visual viewport (mobile keyboard, etc.)
 */
function computeAvailableHeight(node: HTMLElement, scrollport: HTMLElement | null, bottomInset: number): number {
    const nodeRect = node.getBoundingClientRect();

    // Start with window as the boundary
    let visibleBottom = window.innerHeight;

    // If there's a scrollport, use its visible bottom edge
    if (scrollport) {
        const scrollportRect = scrollport.getBoundingClientRect();
        visibleBottom = Math.min(visibleBottom, scrollportRect.bottom);
    }

    // Account for visual viewport (mobile keyboard, zoom, etc.)
    const vv = window.visualViewport;
    if (vv) {
        visibleBottom = Math.min(visibleBottom, vv.offsetTop + vv.height);
    }

    // Available = from node's top to the visible bottom, minus padding
    return Math.round(visibleBottom - nodeRect.top - bottomInset);
}

/**
 * Measures space from the wrapper's top edge to the bottom of the visible scrollport / viewport,
 * clamps to min/max. Height is measured on mount, resize, and ancestor size changes
 * (ResizeObserver), but NOT on scroll (table height stays fixed while scrolling within the page).
 */
export function useFillAvailableViewportHeight(args: UseFillAvailableViewportHeightArgs): {
    wrapperRef: RefObject<HTMLDivElement | null>;
    heightPx: number;
} {
    const { enabled, viewportMaxHeight, viewportMinHeight, viewportBottomInsetPx = DEFAULT_BOTTOM_INSET_PX } = args;

    const maxPx = parseViewportLengthToPx(viewportMaxHeight, DATA_TABLE_DEFAULT_VIEWPORT_MAX_HEIGHT_PX);
    const minPx = viewportMinHeight != null ? parseViewportLengthToPx(viewportMinHeight, 0) : 0;

    const wrapperRef = useRef<HTMLDivElement>(null);

    // Start with max height - this ensures the table is full size on initial render
    const [heightPx, setHeightPx] = useState(maxPx);

    useLayoutEffect(() => {
        if (!enabled) {
            return;
        }

        const el = wrapperRef.current;
        if (!el || typeof window === 'undefined') {
            return;
        }

        const scrollport = findVerticalScrollport(el);

        let rafId = 0;
        let timeoutId: ReturnType<typeof setTimeout>;

        const applyMeasure = (node: HTMLElement) => {
            const available = computeAvailableHeight(node, scrollport, viewportBottomInsetPx);
            const clamped = Math.min(maxPx, Math.max(minPx, available));
            setHeightPx(clamped);
        };

        const scheduleMeasureWhenLaidOut = (attempt: number) => {
            if (attempt > 24) {
                return;
            }
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const node = wrapperRef.current;
                if (!node || document.visibilityState !== 'visible') {
                    scheduleMeasureWhenLaidOut(attempt + 1);
                    return;
                }
                const rect = node.getBoundingClientRect();
                if (rect.width === 0) {
                    scheduleMeasureWhenLaidOut(attempt + 1);
                    return;
                }
                applyMeasure(node);
            });
        };

        const measure = () => {
            const node = wrapperRef.current;
            if (!node) {
                return;
            }

            if (document.visibilityState !== 'visible') {
                return;
            }

            const rect = node.getBoundingClientRect();
            if (rect.width === 0) {
                scheduleMeasureWhenLaidOut(0);
                return;
            }

            applyMeasure(node);
        };

        const measureSoon = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(measure);
        };

        const measureDelayed = () => {
            clearTimeout(timeoutId);
            cancelAnimationFrame(rafId);
            timeoutId = setTimeout(() => {
                rafId = requestAnimationFrame(() => {
                    rafId = requestAnimationFrame(measure);
                });
            }, 0);
        };

        // Initial measurement after layout settles
        measureDelayed();

        // ResizeObserver for element and ancestor size changes (but NOT scroll)
        const ro = new ResizeObserver(measureSoon);
        ro.observe(el);
        if (el.parentElement) {
            ro.observe(el.parentElement);
        }
        if (scrollport && scrollport !== el.parentElement) {
            ro.observe(scrollport);
        }

        // Window resize only - NOT scroll events
        window.addEventListener('resize', measureSoon);

        // Visual viewport resize (mobile keyboard appearing/disappearing)
        const vv = window.visualViewport;
        vv?.addEventListener('resize', measureSoon);

        // Visibility change (tab becomes visible again)
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                measureDelayed();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        // Page show (bfcache restoration)
        window.addEventListener('pageshow', measureDelayed);

        return () => {
            cancelAnimationFrame(rafId);
            clearTimeout(timeoutId);
            ro.disconnect();
            window.removeEventListener('resize', measureSoon);
            vv?.removeEventListener('resize', measureSoon);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pageshow', measureDelayed);
        };
    }, [enabled, maxPx, minPx, viewportBottomInsetPx]);

    return { wrapperRef, heightPx };
}
